import _debounce from 'lodash/debounce';
import React, { PureComponent } from 'react';

import logger from '../../logger';

import InlineDialogFailure from './InlineDialogFailure';

/**
 * The type of the React {@code Component} props of
 * {@link MultiSelectAutocomplete}.
 */
type Props = {

    /**
     * The default value of the selected item.
     */
    defaultValue: Array<Object>,

    /**
     * Optional footer to show as a last element in the results.
     * Should be of type {content: <some content>}.
     */
    footer: Object,

    /**
     * Indicates if the component is disabled.
     */
    isDisabled: boolean,

    /**
     * Text to display while a query is executing.
     */
    loadingMessage: string,

    /**
     * The text to show when no matches are found.
     */
    noMatchesFound: string,

    /**
     * The function called immediately before a selection has been actually
     * selected. Provides an opportunity to do any formatting.
     */
    onItemSelected: Function,

    /**
     * The function called when the selection changes.
     */
    onSelectionChange: Function,

    /**
     * The placeholder text of the input component.
     */
    placeholder: string,

    /**
     * The service providing the search.
     */
    resourceClient: { makeQuery: Function, parseResults: Function },

    /**
     * Indicates if the component should fit the container.
     */
    shouldFitContainer: boolean,

    /**
     * Indicates if we should focus.
     */
    shouldFocus: boolean
};

/**
 * The type of the React {@code Component} state of
 * {@link MultiSelectAutocomplete}.
 */
type State = {

    /**
     * Indicates if the dropdown is open.
     */
    isOpen: boolean,

    /**
     * The text that filters the query result of the search.
     */
    filterValue: string,

    /**
     * Indicates if the component is currently loading results.
     */
    loading: boolean,

    /**
     * Indicates if there was an error.
     */
    error: boolean,

    /**
     * The list of result items.
     */
    items: Array<Object>,

    /**
     * The list of selected items.
     */
    selectedItems: Array<Object>
};

/**
 * A MultiSelect that is also auto-completing.
 */
class MultiSelectAutocomplete extends PureComponent<Props, State> {
    /**
     * Initializes a new {@code MultiSelectAutocomplete} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        const defaultValue = this.props.defaultValue || [];

        this.state = {
            isOpen: false,
            filterValue: '',
            loading: false,
            error: false,
            items: [],
            selectedItems: [ ...defaultValue ]
        };

        this._onFilterChange = this._onFilterChange.bind(this);
        this._onRetry = this._onRetry.bind(this);
        this._onSelectionChange = this._onSelectionChange.bind(this);
        this._sendQuery = _debounce(this._sendQuery.bind(this), 200);
    }

    /**
     * Sets the items to display as selected.
     *
     * @param {Array<Object>} selectedItems - The list of items to display as
     * having been selected.
     * @returns {void}
     */
    setSelectedItems(selectedItems: Array<Object> = []) {
        this.setState({ selectedItems });
    }

    /**
     * Renders the content of this component.
     *
     * @returns {ReactElement}
     */
    render() {
        const shouldFocus = this.props.shouldFocus || false;
        const isDisabled = this.props.isDisabled || false;
        const placeholder = this.props.placeholder || '';
        const noMatchesFound = this.props.noMatchesFound || '';
        const { selectedItems } = this.state;

        return (
            <div
                style = {{ position: 'relative',
                    width: this.props.shouldFitContainer ? '100%' : undefined }}>
                <div
                    style = {{
                        alignItems: 'center',
                        background: '#fafbfc',
                        border: '1px solid #b5cbeb',
                        borderRadius: '8px',
                        boxSizing: 'border-box',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '6px',
                        minHeight: '44px',
                        padding: '8px 10px',
                        width: '100%'
                    }}>
                    {selectedItems.map(selectedItem => (
                        <span
                            key = { selectedItem.value }
                            style = {{
                                alignItems: 'center',
                                background: '#e8eef7',
                                borderRadius: '999px',
                                color: '#253858',
                                display: 'inline-flex',
                                gap: '6px',
                                maxWidth: '100%',
                                padding: '4px 8px'
                            }}>
                            {selectedItem.tag?.elemBefore || selectedItem.elemBefore || null}
                            <span
                                style = {{ overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap' }}>
                                {selectedItem.content}
                            </span>
                            <button
                                aria-label = 'Remove item'
                                onClick = { () => this._removeSelectedItem(selectedItem) }
                                style = {{
                                    background: 'transparent',
                                    border: 0,
                                    color: '#253858',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    lineHeight: 1,
                                    padding: 0
                                }}
                                type = 'button'>
                                ×
                            </button>
                        </span>
                    ))}
                    <input
                        autoFocus = { shouldFocus }
                        className = 'input-control'
                        disabled = { isDisabled }
                        onBlur = { this._onInputBlur }
                        onChange = { e => this._onFilterChange(e.target.value) }
                        onFocus = { this._onInputFocus }
                        placeholder = { placeholder }
                        style = {{
                            background: 'transparent',
                            border: 0,
                            boxSizing: 'border-box',
                            color: '#333333',
                            flex: 1,
                            fontSize: '14px',
                            minWidth: '160px',
                            outline: 'none',
                            padding: '4px 0'
                        }}
                        type = 'text'
                        value = { this.state.filterValue } />
                </div>
                {this.state.isOpen && (
                    <div
                        style = {{
                            background: '#ffffff',
                            border: '1px solid rgba(69, 81, 102, 0.25)',
                            borderRadius: '10px',
                            boxShadow: '0 4px 8px -2px rgba(9, 30, 66, 0.25), 0 0 1px rgba(9, 30, 66, 0.31)',
                            left: 0,
                            marginTop: '6px',
                            maxHeight: '260px',
                            overflowY: 'auto',
                            position: 'absolute',
                            right: 0,
                            top: '100%',
                            zIndex: 20
                        }}>
                        {this.state.loading ? (
                            <div
                                style = {{ color: '#6b778c',
                                    padding: '12px 14px' }}>
                                {this.props.loadingMessage}
                            </div>
                        ) : this.state.items.length
                            ? this.state.items.map(item => this._renderItem(item))
                            : (
                                <div
                                    style = {{ color: '#6b778c',
                                        padding: '12px 14px' }}>
                                    {noMatchesFound}
                                </div>
                            )}
                        {this.props.footer?.content ? (
                            <div
                                style = {{ borderTop: '1px solid #e9edf5',
                                    padding: '10px 14px' }}>
                                {this.props.footer.content}
                            </div>
                        ) : null}
                    </div>
                )}
                { this._renderError() }
            </div>
        );
    }

    _onInputBlur: () => void;

    _onInputBlur() {
        window.setTimeout(() => {
            this.setState({ isOpen: false });
        }, 150);
    }

    _onInputFocus: () => void;

    _onInputFocus() {
        if (this.state.filterValue || this.state.items.length) {
            this.setState({ isOpen: true });
        }
    }

    _onFilterChange: (string) => void;

    /**
     * Sets the state and sends a query on filter change.
     *
     * @param {string} filterValue - The filter text value.
     * @private
     * @returns {void}
     */
    _onFilterChange(filterValue) {
        this.setState({
            // Clean the error if the filterValue is empty.
            error: this.state.error && Boolean(filterValue),
            filterValue,
            isOpen: Boolean(filterValue),
            items: filterValue ? this.state.items : [],
            loading: Boolean(filterValue)
        });
        if (filterValue) {
            this._sendQuery(filterValue);
        }
    }

    _onRetry: () => void;

    /**
     * Retries the query on retry.
     *
     * @private
     * @returns {void}
     */
    _onRetry() {
        this._sendQuery(this.state.filterValue);
    }

    _onSelectionChange: (Object) => void;

    /**
     * Updates the selected items when a selection event occurs.
     *
     * @param {Object} item - The selected item.
     * @private
     * @returns {void}
     */
    _onSelectionChange(item) {
        const existing
            = this.state.selectedItems.find(k => k.value === item.value);
        let selectedItems = this.state.selectedItems;

        if (existing) {
            selectedItems = selectedItems.filter(k => k !== existing);
        } else {
            selectedItems = [ ...selectedItems, this.props.onItemSelected(item) ];
        }
        this.setState({
            filterValue: '',
            isOpen: false,
            items: [],
            loading: false,
            selectedItems
        });

        if (this.props.onSelectionChange) {
            this.props.onSelectionChange(selectedItems);
        }
    }

    /**
     * Renders the error UI.
     *
     * @returns {ReactElement|null}
     */
    _renderError() {
        if (!this.state.error) {
            return null;
        }

        return (
            <div className = 'autocomplete-error'>
                <InlineDialogFailure
                    onRetry = { this._onRetry } />
            </div>
        );
    }

    _removeSelectedItem: (Object) => void;

    _removeSelectedItem(item) {
        const selectedItems = this.state.selectedItems.filter(k => k.value !== item.value);

        this.setState({ selectedItems });
        this.props.onSelectionChange?.(selectedItems);
    }

    _renderItem: (Object) => React$Node;

    _renderItem(item) {
        return (
            <button
                className = { item.className || '' }
                disabled = { item.isDisabled }
                key = { item.value }
                onClick = { () => this._onSelectionChange(item) }
                style = {{
                    alignItems: 'flex-start',
                    background: 'transparent',
                    border: 0,
                    color: '#172b4d',
                    cursor: item.isDisabled ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: item.description ? '4px' : 0,
                    opacity: item.isDisabled ? 0.6 : 1,
                    padding: '10px 14px',
                    textAlign: 'left',
                    width: '100%'
                }}
                type = 'button'>
                <span
                    style = {{ alignItems: 'center',
                        display: 'inline-flex',
                        gap: '10px' }}>
                    {item.elemBefore || null}
                    <span>{item.content}</span>
                </span>
                {item.description ? (
                    <span
                        style = {{ color: '#6b778c',
                            fontSize: '12px',
                            lineHeight: 1.4 }}>
                        {item.description}
                    </span>
                ) : null}
            </button>
        );
    }

    _sendQuery: (string) => void;

    /**
     * Sends a query to the resourceClient.
     *
     * @param {string} filterValue - The string to use for the search.
     * @returns {void}
     */
    _sendQuery(filterValue) {
        if (!filterValue) {
            return;
        }

        this.setState({
            error: false
        });

        const resourceClient = this.props.resourceClient || {
            makeQuery: () => Promise.resolve([]),
            parseResults: results => results
        };

        resourceClient.makeQuery(filterValue)
            .then(results => {
                if (this.state.filterValue !== filterValue) {
                    this.setState({
                        error: false
                    });

                    return;
                }
                const items = resourceClient.parseResults(results) || [];

                this.setState({
                    items,
                    isOpen: true,
                    loading: false,
                    error: false
                });
            })
            .catch(error => {
                logger.error('MultiSelectAutocomplete error in query', error);

                this.setState({
                    error: true,
                    loading: false,
                    isOpen: false
                });
            });
    }
}

export default MultiSelectAutocomplete;
